import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactApexChart from 'react-apexcharts';
import { Card, CardBody, Row, Col } from 'reactstrap';
import { fetchDashboardData } from '../../slices/analytics/thunk';

const VisitorAnalytics = () => {
  const dispatch = useDispatch();
  const { dashboardData, loading, error } = useSelector((state) => state.Analytics);

  useEffect(() => {
    dispatch(fetchDashboardData());
  }, [dispatch]);

  // Process data for monthly multi-line chart
  const processedMonthlyData = useMemo(() => {
    const monthlyData = dashboardData?.graphs?.monthly || {};

    const totalData = monthlyData.total || [];
    const activeData = monthlyData.active || [];
    const inactiveData = monthlyData.inactive || [];

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Extract counts for each series
    const getSeriesData = (data) => {
      if (!Array.isArray(data) || data.length === 0) {
        return new Array(12).fill(0);
      }

      let seriesData = [];

      months.forEach((monthName, index) => {
        const found = data.find(item => item.month === monthName);
        seriesData.push(found ? found.count : 0);
      });

      return seriesData;
    };

    const totalSeries = getSeriesData(totalData);
    const activeSeries = getSeriesData(activeData);
    const inactiveSeries = getSeriesData(inactiveData);

    // Get current month index (0-11)
    const currentMonthIndex = new Date().getMonth();

    // Rotate data to show current month at the end
    const rotateData = (data) => {
      return [
        ...data.slice(currentMonthIndex + 1),
        ...data.slice(0, currentMonthIndex + 1)
      ];
    };

    // Rotate categories to match the data rotation
    const rotatedCategories = rotateData(months);

    return {
      series: [
        {
          name: 'Total Users',
          data: rotateData(totalSeries),
          color: '#556ee6' // Blue for total users
        },
        {
          name: 'Active Users',
          data: rotateData(activeSeries),
          color: '#34c38f' // Green for active users
        },
        {
          name: 'Inactive Users',
          data: rotateData(inactiveSeries),
          color: '#f46a6a' // Red for inactive users
        }
      ],
      categories: rotatedCategories
    };
  }, [dashboardData]);

  // Process data for subscription pie chart
  const subscriptionData = useMemo(() => {
    const subscriptions = dashboardData?.subscriptions || {};

    const activeSubscriptions = subscriptions.activeSubscriptions || 0;
    const inactiveSubscriptions = subscriptions.inactiveSubscriptions || 0;

    return {
      series: [activeSubscriptions, inactiveSubscriptions],
      labels: ['Active Subscriptions', 'Inactive Subscriptions'],
      total: activeSubscriptions + inactiveSubscriptions
    };
  }, [dashboardData]);

  // Process data for active/inactive users pie chart
  const userStatusData = useMemo(() => {
    const users = dashboardData?.users || {};

    const activeUsers = users.active || 0;
    const inactiveUsers = users.inactive || 0;

    return {
      series: [activeUsers, inactiveUsers],
      labels: ['Active Users', 'Inactive Users'],
      total: activeUsers + inactiveUsers
    };
  }, [dashboardData]);

  // Process data for content distribution pie chart
  const contentData = useMemo(() => {
    const content = dashboardData?.content || {};

    const totalNews = content.news || 0;
    const totalPosts = content.posts || 0;

    return {
      series: [totalNews, totalPosts],
      labels: ['Total News', 'Total Posts'],
      total: totalNews + totalPosts
    };
  }, [dashboardData]);

  // Calculate Y-axis max for line chart
  const calculateYAxisMax = (seriesData) => {
    if (!seriesData || seriesData.length === 0) return 100;

    const allValues = seriesData.flatMap(series => series.data);
    const maxUsers = Math.max(...allValues);

    if (maxUsers === 0) return 100;
    const withMargin = maxUsers * 1.25;
    const rounded = Math.ceil(withMargin / 5) * 5;
    return Math.max(rounded, 100);
  };

  // Multi-line chart options (Trellis/Lattice chart)
  const lineChartOptions = useMemo(() => {
    const yAxisMax = calculateYAxisMax(processedMonthlyData.series);

    return {
      chart: {
        height: 350,
        type: 'line',
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 3,
        curve: 'smooth'
      },
      markers: {
        size: 5,
        hover: {
          size: 7
        }
      },
      xaxis: {
        categories: processedMonthlyData.categories,
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          style: {
            colors: '#6c757d',
            fontSize: '12px',
            fontFamily: 'inherit',
            fontWeight: 400,
          },
        }
      },
      yaxis: {
        labels: {
          formatter: function (val) {
            return Math.round(val).toString();
          },
          style: {
            colors: '#6c757d',
            fontSize: '12px',
            fontFamily: 'inherit',
            fontWeight: 400,
          },
        },
        min: 0,
        max: yAxisMax,
        tickAmount: 6
      },
      colors: processedMonthlyData.series.map(series => series.color),
      grid: {
        borderColor: '#f1f1f1',
        row: {
          colors: ['transparent', 'transparent'],
          opacity: 0.5
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (val) {
            return val + ' users';
          }
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        floating: false,
        offsetY: -10,
        offsetX: 0,
        labels: {
          colors: '#6c757d',
          useSeriesColors: false
        },
      }
    };
  }, [processedMonthlyData]);

  // Common pie chart options (without center total)
  const getPieChartOptions = (labels, colors) => {
    return {
      chart: {
        type: 'pie',
        height: 320,
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: false // Remove center labels
            }
          }
        }
      },
      colors: colors,
      labels: labels,
      dataLabels: {
        enabled: true,
        formatter: function (val, opts) {
          return opts.w.globals.series[opts.seriesIndex].toString();
        },
        style: {
          fontSize: '12px',
          fontWeight: '600',
          colors: ["#ffffff"]
        }
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toString();
          }
        }
      }
    };
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center">Loading dashboard data...</div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <div className="text-center text-danger">Error: {error}</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Row>
        {/* Subscription Status Pie Chart */}
        <Col xl={6}>
          <Card className='mb-4'>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <h4 className="card-title mb-0 flex-grow-1">Subscription Status</h4>
              </div>
              <ReactApexChart
                options={getPieChartOptions(
                  subscriptionData.labels,
                  ['#34c38f', '#f46a6a']
                )}
                series={subscriptionData.series}
                type="pie"
                height={320}
              />
            </CardBody>
          </Card>
        </Col>

        {/* Content Distribution Pie Chart */}
        <Col xl={6}>
          <Card className='mb-4'>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <h4 className="card-title mb-0 flex-grow-1">Content Distribution</h4>
              </div>
              <ReactApexChart
                options={getPieChartOptions(
                  contentData.labels,
                  ['#556ee6', '#e83e8c']
                )}
                series={contentData.series}
                type="pie"
                height={320}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center mb-2">
                <h4 className="card-title mb-0 flex-grow-1">Monthly User Analytics</h4>
              </div>
              <ReactApexChart
                options={lineChartOptions}
                series={processedMonthlyData.series}
                type="line"
                height={350}
              />
              <div className="text-center mt-2">
                <small className="text-muted">
                  Showing last 12 months with current month ({new Date().toLocaleString('default', { month: 'long' })}) at the end
                </small>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default VisitorAnalytics;